import { useContext, useEffect, useMemo, useState } from "react";
import type { Dayjs } from "dayjs";

import { SIDER_TABS } from "../../../utils/Constants";
import AppContext from "../../../context/AppContext";
import {
  usePaymentReporting,
  type ExportRow,
} from "../../../queries/payments/usePaymentReporting";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";

import PaymentReportingHeader from "./PaymentReportingHeader/PaymentReportingHeader";
import DepositsSection from "./DepositsSection/DepositsSection";
import WithdrawalsSection from "./WithdrawalsSection/WithdrawalsSection";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import styles from "./PaymentReporting.module.css";
import { getAdminNow } from "../../../utils/dateService";

type DateRange = [Dayjs, Dayjs];

const PaymentReporting: React.FC = () => {
  usePageTitle(AppStrings.PAYMENT_REPORTS);

  const { setActiveSidebarTab } = useContext(AppContext);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.PAYMENTS, "REPORTS");
  }, [setActiveSidebarTab]);

  const [dates, setDates] = useState<DateRange>(() => [
    getAdminNow().startOf("month"),
    getAdminNow().endOf("day"),
  ]);

  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data, isFetching } = usePaymentReporting(dateFilters);
  const currencySymbol = useCurrencySymbol();

  // Export covers top-level providers only. Backend doesn't ship `children`
  // today (deposit method/card subtype is out of scope for v2's first
  // release); revisit and flatten recursively when drilldown lands.
  const depositsExportRows = useMemo<ExportRow[]>(() => {
    if (!data) return [];
    const rows: ExportRow[] = data.deposits.providers.map((row) => ({
      section: "Deposits",
      provider: row.provider,
      count: row.deposits,
      amount: row.amount,
      acceptance_rate_pct: row.acceptance_rate,
    }));
    rows.push({
      section: "Deposits",
      provider: "TOTAL",
      count: data.deposits.kpis.total_count.value,
      amount: data.deposits.kpis.total_amount.value,
      acceptance_rate_pct: data.deposits.kpis.acceptance_rate.value,
    });
    return rows;
  }, [data]);

  const withdrawalsExportRows = useMemo<ExportRow[]>(() => {
    if (!data) return [];
    return [
      {
        section: "Withdrawals",
        provider: "TOTAL",
        count: data.withdrawals.kpis.total_count.value,
        amount: data.withdrawals.kpis.total_amount.value,
        acceptance_rate_pct: data.withdrawals.kpis.acceptance_rate.value,
      },
    ];
  }, [data]);

  const allExportRows = useMemo<ExportRow[]>(
    () => [...depositsExportRows, ...withdrawalsExportRows],
    [depositsExportRows, withdrawalsExportRows],
  );

  return (
    <>
      <div className={styles.container}>
        <PaymentReportingHeader
          dates={dates}
          setDates={setDates}
          exportRows={allExportRows}
        />
        <DepositsSection
          deposits={data?.deposits}
          isLoading={isFetching}
          exportRows={depositsExportRows}
          currencySymbol={currencySymbol}
        />
        <WithdrawalsSection
          withdrawals={data?.withdrawals}
          isLoading={isFetching}
          exportRows={withdrawalsExportRows}
          currencySymbol={currencySymbol}
        />
      </div>
    </>
  );
};

export default PaymentReporting;
